/**
 * The 2D map draws every module of a state as one SVG path (phase 6): a 500-module array is a handful of elements
 * instead of thousands, so dragging, turning and panning it stays smooth. Pure: screen corners in, path data out
 * (tested in perf.test.ts); SketchCanvas.tsx draws the paths with the classes in sketch.css.
 */
import type { Vec2 } from "../editor/geo.ts";

/** One module on screen: its key and its four corners in pixels. */
export interface ScreenPanel {
  key: string;
  corners: Vec2[];
}

export interface PanelBatches {
  /** Modules in their own look, by state (one path each). */
  plain: string;
  selected: string;
  conflict: string;
  /** Just placed or just snapped (drawn with their animation; also in `plain`/`selected` underneath). */
  fresh: string;
  snapped: string;
  /** The cell lines of every module large enough to read them. */
  cells: string;
  /** Heatmap: modules grouped by their colour (solar access to the percent). */
  heat: { access: number; d: string }[];
  /** Heatmap labels (the percentage) on modules large enough to read one. */
  labels: { key: string; at: Vec2; text: string }[];
}

const f = (v: number) => v.toFixed(1);

/** A closed ring as path data. */
export function ringPath(c: Vec2[]): string {
  let d = "";
  for (let i = 0; i < c.length; i++) d += `${i ? "L" : "M"}${f(c[i]![0])},${f(c[i]![1])}`;
  return c.length ? `${d}Z` : "";
}

/** The three cell lines of a module (the half-cut split across and two lines along), as path data. */
export function cellPath(c: Vec2[]): string {
  if (c.length !== 4) return "";
  const [a, b, d, e] = c as [Vec2, Vec2, Vec2, Vec2];
  const l01 = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const l12 = Math.hypot(d[0] - b[0], d[1] - b[1]);
  if (Math.min(l01, l12) < 7) return ""; // too small to read
  const lerp = (p: Vec2, q: Vec2, t: number): Vec2 => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
  // long sides: a→b and e→d when l01 ≥ l12, else b→d and a→e
  const [s0, s1, t0, t1] = l01 >= l12 ? [a, b, e, d] : [a, e, b, d];
  const segs: [Vec2, Vec2][] = [
    [lerp(s0, s1, 0.5), lerp(t0, t1, 0.5)],
    [lerp(s0, t0, 1 / 3), lerp(s1, t1, 1 / 3)],
    [lerp(s0, t0, 2 / 3), lerp(s1, t1, 2 / 3)],
  ];
  return segs.map(([p, q]) => `M${f(p[0])},${f(p[1])}L${f(q[0])},${f(q[1])}`).join("");
}

/** Every module's path, grouped by how it is drawn. A module in conflict is drawn as a conflict even when selected
 * (the warning wins, as before); the heatmap colours replace the plain look, and selected modules keep their outline
 * over them (`selected`, drawn without a fill in the heatmap). */
export function panelBatches(
  panels: ScreenPanel[],
  state: { selected: ReadonlySet<string>; conflicts: Record<string, unknown[] | undefined>; heat?: ReadonlyMap<string, number> | null; fresh?: ReadonlySet<string>; snapped?: ReadonlySet<string> },
): PanelBatches {
  const out: PanelBatches = { plain: "", selected: "", conflict: "", fresh: "", snapped: "", cells: "", heat: [], labels: [] };
  const heat = new Map<number, string>();
  for (const p of panels) {
    if (p.corners.length < 3) continue;
    const ring = ringPath(p.corners);
    const access = state.heat?.get(p.key);
    if (state.conflicts[p.key]?.length) out.conflict += ring;
    else if (access != null) {
      const k = Math.round(access * 100);
      heat.set(k, (heat.get(k) ?? "") + ring);
      if (state.selected.has(p.key)) out.selected += ring; // outlined over its colour
    } else if (state.selected.has(p.key)) out.selected += ring;
    else out.plain += ring;
    if (state.fresh?.has(p.key)) out.fresh += ring;
    if (state.snapped?.has(p.key)) out.snapped += ring;
    out.cells += cellPath(p.corners);
    if (access != null && p.corners.length === 4 && Math.hypot(p.corners[1]![0] - p.corners[0]![0], p.corners[1]![1] - p.corners[0]![1]) > 22) {
      const at: Vec2 = [p.corners.reduce((a, q) => a + q[0], 0) / 4, p.corners.reduce((a, q) => a + q[1], 0) / 4];
      out.labels.push({ key: p.key, at, text: String(Math.round(access * 100)) });
    }
  }
  out.heat = [...heat].sort((a, b) => a[0] - b[0]).map(([k, d]) => ({ access: k / 100, d }));
  return out;
}

/** The modules' colours (sketch.css tokens, read by the canvas once a draw). */
export interface PanelLook {
  fill: string;
  edge: string;
  cell: string;
  selectedFill: string;
  selected: string;
  conflict: string;
  /** the conflict hatch (a pattern), else a colour */
  conflictFill: unknown;
  heat: (access: number) => string;
}

/** The part of a 2D canvas context the modules need (a fake in the tests). */
export interface PanelPen {
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineJoin: string;
  fill(path: unknown): void;
  stroke(path: unknown): void;
}

/** Draws the batches on the modules' canvas, bottom to top: plain, heatmap, selected, conflicts, cell lines. In the
 * heatmap modules are edged white and their cells darker (as the SVG did). */
export function drawPanels(g: PanelPen, b: PanelBatches, look: PanelLook, toPath: (d: string) => unknown): void {
  const heatOn = b.heat.length > 0;
  g.lineJoin = "round";
  const layer = (d: string, fill: unknown, stroke: string, width: number) => {
    if (!d) return;
    const p = toPath(d);
    if (fill != null) {
      g.fillStyle = fill;
      g.fill(p);
    }
    g.strokeStyle = stroke;
    g.lineWidth = width;
    g.stroke(p);
  };
  layer(b.plain, look.fill, heatOn ? "rgba(255, 255, 255, 0.9)" : look.edge, 0.9);
  for (const h of b.heat) layer(h.d, look.heat(h.access), "rgba(255, 255, 255, 0.9)", 0.9);
  layer(b.selected, heatOn ? null : look.selectedFill, look.selected, 2.2);
  layer(b.conflict, look.conflictFill, look.conflict, 2.2);
  if (b.cells) {
    g.strokeStyle = heatOn ? "rgba(0, 0, 0, 0.18)" : look.cell;
    g.lineWidth = 0.75;
    g.stroke(toPath(b.cells));
  }
}
